/**
 * Signup Requests View
 * Platform prospect queue — review, edit, email, and launch onboarding.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icons } from '../common/Icons';
import { signupRequestsService, emailTemplatesService } from '../../services/api';
import { formatDate, formatDateTime, formatRelativeTime } from '../../utils/formatters';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'provisioned', label: 'Provisioned' },
  { value: 'declined', label: 'Declined' },
];

const STATUS_BADGE = {
  new: 'warning',
  contacted: 'neutral',
  provisioned: 'success',
  declined: 'neutral',
};

const PLAN_LABELS = {
  producer: 'Producer',
  trades: 'Trades',
  grower: 'Grower',
  'market-director': 'Market Director',
  'market-vendor': 'Market Vendor',
  'food-trailer': 'Food Trailer',
  rancher: 'Rancher',
  'farm-kitchen': 'Farm Kitchen',
  'market-and-trailer': 'Market & Trailer',
  'i-want-it-all': 'I Want It All',
  unsure: 'Not sure',
};

function planLabel(slug) {
  if (!slug) return 'Not specified';
  return PLAN_LABELS[slug] || slug;
}

function emptyEditForm() {
  return {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    business_name: '',
    plan_interest: 'unsure',
    notes: '',
    admin_notes: '',
  };
}

const SignupRequestsView = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm());
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  const [viewingSend, setViewingSend] = useState(null);

  const highlightId = searchParams.get('highlight');

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await signupRequestsService.getAll({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 100,
      });
      setRequests(response.data || []);
    } catch (err) {
      console.error('Failed to load signup requests:', err);
      setError('Failed to load signup requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    if (highlightId && requests.length > 0) {
      setSelectedId(highlightId);
      searchParams.delete('highlight');
      setSearchParams(searchParams, { replace: true });
    }
  }, [highlightId, requests, searchParams, setSearchParams]);

  const selected = requests.find((r) => r.id === selectedId) || null;

  const loadEmailHistory = useCallback(async (requestId) => {
    if (!requestId) {
      setEmailHistory([]);
      return;
    }
    try {
      setEmailHistoryLoading(true);
      const response = await signupRequestsService.getEmails(requestId);
      setEmailHistory(response.data || []);
    } catch (err) {
      console.error('Failed to load email history:', err);
      setEmailHistory([]);
    } finally {
      setEmailHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmailHistory(selectedId);
  }, [selectedId, loadEmailHistory]);

  useEffect(() => {
    if (!selected) {
      setEditForm(emptyEditForm());
      return;
    }
    setEditForm({
      first_name: selected.first_name || '',
      last_name: selected.last_name || '',
      email: selected.email || '',
      phone: selected.phone || '',
      business_name: selected.business_name || '',
      plan_interest: selected.plan_interest || 'unsure',
      notes: selected.notes || '',
      admin_notes: selected.admin_notes || '',
    });
  }, [selected?.id]);

  const handleSelect = (request) => {
    setSelectedId(request.id);
    setEmailSuccess('');
    setViewingSend(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      setActionLoading(true);
      await signupRequestsService.update(id, { status });
      await loadRequests();
      if (status === 'declined') {
        setSelectedId(null);
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      setError(err.response?.data?.message || 'Failed to update request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      setError(null);
      await signupRequestsService.update(selected.id, {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        phone: editForm.phone,
        business_name: editForm.business_name,
        plan_interest: editForm.plan_interest,
        notes: editForm.notes,
        admin_notes: editForm.admin_notes,
      });
      await loadRequests();
    } catch (err) {
      console.error('Failed to save signup request:', err);
      setError(err.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleStartOnboarding = async (request) => {
    if (request.status === 'new') {
      await signupRequestsService.update(request.id, { status: 'contacted' }).catch(() => {});
    }
    navigate(`/admin/onboarding?request=${request.id}`);
  };

  const openEmailModal = async () => {
    if (!selected) return;
    setEmailSuccess('');
    setError(null);
    try {
      const response = await emailTemplatesService.getAll({
        purpose: 'signup_outreach',
        active_only: true,
      });
      const list = response.data || [];
      // Also allow general templates
      const general = await emailTemplatesService.getAll({ active_only: true });
      const combined = [...list];
      for (const t of general.data || []) {
        if (t.purpose === 'change_management') continue;
        if (!combined.find((x) => x.id === t.id)) combined.push(t);
      }
      setTemplates(combined);

      const defaultTpl = combined.find((t) => t.purpose === 'signup_outreach') || combined[0];
      if (defaultTpl) {
        setEmailTemplateId(defaultTpl.id);
        setEmailSubject(defaultTpl.subject);
        setEmailMessage(defaultTpl.body_text);
      } else {
        setEmailTemplateId('');
        setEmailSubject('Good to meet you at the Southern Family Farm and Food Systems conference');
        setEmailMessage(
          `Hi ${selected.first_name || ''},\n\nIt was a pleasure meeting you at the Southern Family Farm and Food Systems conference. Thank you for your interest in Business Manager.\n\nI'd welcome the chance to continue the conversation — or simply reply to this email with what would be most useful next.\n\nBest regards,\nRobin Hood\nCR Hood Solutions`
        );
      }
      setShowEmailModal(true);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setEmailTemplateId('');
      setEmailSubject('Good to meet you at the Southern Family Farm and Food Systems conference');
      setEmailMessage(
        `Hi ${selected.first_name || ''},\n\nIt was a pleasure meeting you...\n\nBest regards,\nRobin Hood`
      );
      setShowEmailModal(true);
    }
  };

  const handleTemplatePick = (id) => {
    setEmailTemplateId(id);
    const tpl = templates.find((t) => t.id === id);
    if (tpl) {
      setEmailSubject(tpl.subject);
      setEmailMessage(tpl.body_text);
    }
  };

  const handleSendEmail = async () => {
    if (!selected) return;
    try {
      setSendingEmail(true);
      setError(null);
      await signupRequestsService.sendEmail(selected.id, {
        subject: emailSubject,
        message: emailMessage,
        template_id: emailTemplateId || undefined,
      });
      setShowEmailModal(false);
      setEmailSuccess('Email sent. Request marked contacted if it was new.');
      await Promise.all([loadRequests(), loadEmailHistory(selected.id)]);
    } catch (err) {
      console.error('Failed to send email:', err);
      setError(err.response?.data?.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          Inbound subscription requests from public forms. Provision tenants via the onboarding wizard.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => navigate('/admin/email-templates')}
          >
            <Icons.FileText size={16} />
            Email templates
          </button>
          <button className="btn btn-secondary btn-sm" onClick={loadRequests} disabled={loading}>
            <Icons.RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {emailSuccess && (
        <div className="alert alert-success mb-3">
          <Icons.CheckCircle size={18} />
          <span>{emailSuccess}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`btn btn-sm ${statusFilter === opt.value ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setStatusFilter(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 420px' : '1fr', gap: '24px' }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div className="empty-state" style={{ padding: '48px' }}>
              <Icons.Loader size={40} className="spin" />
              <p>Loading requests...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="empty-state" style={{ padding: '48px' }}>
              <Icons.Mail size={48} />
              <h3>No signup requests</h3>
              <p>
                {statusFilter === 'new'
                  ? 'New requests from busmgr.com and other public forms will appear here.'
                  : 'No requests match this filter.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {requests.map((request) => (
                <button
                  key={request.id}
                  type="button"
                  onClick={() => handleSelect(request)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '16px 20px',
                    border: 'none',
                    borderBottom: '1px solid var(--border-color)',
                    background: selectedId === request.id ? 'var(--bg-secondary)' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    color: 'var(--text-primary)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '15px', color: 'var(--text-primary)' }}>{request.business_name}</strong>
                      <span className={`badge badge-${STATUS_BADGE[request.status] || 'neutral'}`}>
                        {request.status}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      {request.first_name} {request.last_name} &middot; {request.email}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      {planLabel(request.plan_interest)} &middot; {request.source || 'busmgr.com'} &middot;{' '}
                      {formatRelativeTime(request.submitted_at)}
                    </div>
                  </div>
                  <Icons.ChevronRight size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div className="card">
            <div className="card-header" style={{ paddingBottom: '12px' }}>
              <h3 className="card-title" style={{ marginBottom: '4px' }}>
                {editForm.business_name || 'Signup request'}
              </h3>
              <p className="card-subtitle">Submitted {formatDate(selected.submitted_at)}</p>
            </div>

            <div className="form-row" style={{ marginBottom: '12px' }}>
              <div className="form-group">
                <label className="form-label">First name</label>
                <input
                  className="form-control"
                  value={editForm.first_name}
                  onChange={(e) => handleEditChange('first_name', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last name</label>
                <input
                  className="form-control"
                  value={editForm.last_name}
                  onChange={(e) => handleEditChange('last_name', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-control"
                type="email"
                value={editForm.email}
                onChange={(e) => handleEditChange('email', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                className="form-control"
                value={editForm.phone}
                onChange={(e) => handleEditChange('phone', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Business name</label>
              <input
                className="form-control"
                value={editForm.business_name}
                onChange={(e) => handleEditChange('business_name', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Plan interest</label>
              <select
                className="form-control"
                value={editForm.plan_interest}
                onChange={(e) => handleEditChange('plan_interest', e.target.value)}
              >
                {Object.entries(PLAN_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
              Source: {selected.source || 'busmgr.com'}
            </div>

            <div className="form-group">
              <label className="form-label">Prospect notes</label>
              <textarea
                className="form-control"
                rows={3}
                value={editForm.notes}
                onChange={(e) => handleEditChange('notes', e.target.value)}
              />
            </div>

            {selected.provisioned_tenant_id && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Provisioned tenant</div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => navigate(`/admin/tenants/${selected.provisioned_tenant_id}`)}
                >
                  {selected.provisioned_tenant_name || selected.provisioned_tenant_slug}
                </button>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Admin notes</label>
              <textarea
                className="form-control"
                rows={3}
                value={editForm.admin_notes}
                onChange={(e) => handleEditChange('admin_notes', e.target.value)}
                placeholder="Internal notes (not visible to prospect)"
              />
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%', marginBottom: '12px' }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {selected.status !== 'declined' && selected.status !== 'provisioned' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openEmailModal}
                  disabled={actionLoading || sendingEmail}
                >
                  <Icons.Send size={18} />
                  Send email
                </button>
              )}
              {selected.status !== 'provisioned' && selected.status !== 'declined' && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleStartOnboarding(selected)}
                  disabled={actionLoading}
                >
                  <Icons.Rocket size={18} />
                  Start onboarding
                </button>
              )}
              {selected.status === 'new' && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleStatusUpdate(selected.id, 'contacted')}
                  disabled={actionLoading}
                >
                  Mark contacted
                </button>
              )}
              {selected.status !== 'declined' && selected.status !== 'provisioned' && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleStatusUpdate(selected.id, 'declined')}
                  disabled={actionLoading}
                >
                  Decline
                </button>
              )}
            </div>

            {(selected.status !== 'new' || emailHistory.length > 0) && (
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                <div className="form-label" style={{ marginBottom: '8px' }}>Email history</div>
                {emailHistoryLoading ? (
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>Loading…</p>
                ) : emailHistory.length === 0 ? (
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>No emails sent</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {emailHistory.map((send) => (
                      <button
                        key={send.id}
                        type="button"
                        onClick={() => setViewingSend(send)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 0',
                          border: 'none',
                          borderBottom: '1px solid var(--border-color)',
                          background: 'transparent',
                          cursor: 'pointer',
                          textAlign: 'left',
                          width: '100%',
                          color: 'var(--text-primary)',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                            {formatDateTime(send.sent_at)}
                            {send.status === 'failed' ? ' · failed' : ''}
                          </div>
                          <div
                            style={{
                              fontSize: '13px',
                              marginTop: '2px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {send.subject}
                          </div>
                        </div>
                        <Icons.ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {viewingSend && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
          onClick={() => setViewingSend(null)}
        >
          <div
            className="card"
            style={{ width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title" style={{ margin: 0 }}>Sent email</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setViewingSend(null)}>
                <Icons.X size={16} />
              </button>
            </div>

            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
              {formatDateTime(viewingSend.sent_at)}
              {viewingSend.status === 'failed' ? ' · failed' : ''}
            </div>

            <div className="form-group">
              <label className="form-label">To</label>
              <input className="form-control" value={viewingSend.to_email || ''} readOnly />
            </div>

            <div className="form-group">
              <label className="form-label">Subject</label>
              <input className="form-control" value={viewingSend.subject || ''} readOnly />
            </div>

            {viewingSend.template_name && (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                Template: {viewingSend.template_name}
              </div>
            )}

            {viewingSend.status === 'failed' && viewingSend.error_message && (
              <div className="alert alert-warning mb-3">
                <Icons.AlertCircle size={18} />
                <span>{viewingSend.error_message}</span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Message</label>
              <pre
                style={{
                  margin: 0,
                  padding: '12px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'inherit',
                  color: 'var(--text-primary)',
                }}
              >
                {viewingSend.body_text || ''}
              </pre>
            </div>
          </div>
        </div>
      )}

      {showEmailModal && selected && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
          onClick={() => !sendingEmail && setShowEmailModal(false)}
        >
          <div
            className="card"
            style={{ width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title" style={{ margin: 0 }}>Send email</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowEmailModal(false)} disabled={sendingEmail}>
                <Icons.X size={16} />
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">To</label>
              <input className="form-control" value={selected.email || ''} disabled />
            </div>

            <div className="form-group">
              <label className="form-label">Template</label>
              <select
                className="form-control"
                value={emailTemplateId}
                onChange={(e) => handleTemplatePick(e.target.value)}
              >
                <option value="">Custom (no template)</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <p className="form-hint">
                Merge fields: {'{{first_name}}'}, {'{{last_name}}'}, {'{{business_name}}'}, {'{{email}}'}
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Subject</label>
              <input
                className="form-control"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea
                className="form-control"
                rows={12}
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEmailModal(false)}
                disabled={sendingEmail}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
              >
                {sendingEmail ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SignupRequestsView;
